import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { X, ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";
import type { TourStep } from "@shared/schema";

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourOverlayProps {
  tourId: string;
  tourTitle: string;
  steps: TourStep[];
  currentStepIndex: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
  onComplete: () => void;
}

function VideoEmbed({ url }: { url: string }) {
  const getEmbedUrl = (videoUrl: string): string | null => {
    // YouTube
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0&modestbranding=1`;
    }

    // Vimeo
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?byline=0&portrait=0`;
    }

    return null;
  };

  const embedUrl = getEmbedUrl(url);

  if (!embedUrl) {
    return (
      <div className="bg-muted rounded-lg p-4 text-center text-sm text-muted-foreground">
        Invalid video URL
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
      <iframe
        src={embedUrl}
        className="absolute top-0 left-0 w-full h-full rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Tour video"
      />
    </div>
  );
}

export function TourOverlay({
  tourId,
  tourTitle,
  steps,
  currentStepIndex,
  onNext,
  onPrevious,
  onClose,
  onComplete,
}: TourOverlayProps) {
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Calculate spotlight position for target element
  const updateSpotlightPosition = () => {
    if (!currentStep?.targetSelector) {
      setSpotlightPosition(null);
      return;
    }

    const targetElement = document.querySelector(currentStep.targetSelector);
    if (!targetElement) {
      setSpotlightPosition(null);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8; // Extra space around the element

    setSpotlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Auto-scroll target into view if not visible
    targetElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  useEffect(() => {
    updateSpotlightPosition();

    // Listen for window resize
    const handleResize = () => updateSpotlightPosition();
    window.addEventListener("resize", handleResize);

    // Listen for scroll
    const handleScroll = () => updateSpotlightPosition();
    window.addEventListener("scroll", handleScroll, true);

    // Use MutationObserver to handle dynamic UI changes
    const observer = new MutationObserver(() => {
      updateSpotlightPosition();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Lock scroll on body
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      observer.disconnect();
      document.body.style.overflow = "";
    };
  }, [currentStep?.targetSelector]);

  // Calculate card position (appears to the right or below the spotlight)
  const getCardPosition = (): React.CSSProperties => {
    if (!spotlightPosition) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "600px",
        width: "90%",
      };
    }

    const cardWidth = 400;
    const cardMaxHeight = 600;
    const gap = 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = spotlightPosition.top;
    let left = spotlightPosition.left + spotlightPosition.width + gap;

    // If card would overflow right, try positioning on the left
    if (left + cardWidth > viewportWidth - gap) {
      left = spotlightPosition.left - cardWidth - gap;
    }

    // If still overflows or negative, center it below the spotlight
    if (left < gap || left + cardWidth > viewportWidth - gap) {
      left = Math.max(gap, Math.min(viewportWidth - cardWidth - gap, spotlightPosition.left));
      top = spotlightPosition.top + spotlightPosition.height + gap;
    }

    // Ensure card stays within viewport vertically
    if (top + cardMaxHeight > viewportHeight - gap) {
      top = Math.max(gap, viewportHeight - cardMaxHeight - gap);
    }

    return {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      maxWidth: `${cardWidth}px`,
      maxHeight: `${cardMaxHeight}px`,
      overflow: "auto",
    };
  };

  const overlayContent = (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999]"
      style={{ pointerEvents: "auto" }}
    >
      {/* Spotlight overlay using separate div layers to create a true cutout */}
      {spotlightPosition ? (
        <>
          {/* Top bar */}
          <div
            className="absolute left-0 right-0 bg-black/70 transition-all duration-300"
            style={{
              top: 0,
              height: `${spotlightPosition.top}px`,
            }}
            onClick={onClose}
          />
          {/* Bottom bar */}
          <div
            className="absolute left-0 right-0 bg-black/70 transition-all duration-300"
            style={{
              top: `${spotlightPosition.top + spotlightPosition.height}px`,
              bottom: 0,
            }}
            onClick={onClose}
          />
          {/* Left bar */}
          <div
            className="absolute left-0 bg-black/70 transition-all duration-300"
            style={{
              top: `${spotlightPosition.top}px`,
              width: `${spotlightPosition.left}px`,
              height: `${spotlightPosition.height}px`,
            }}
            onClick={onClose}
          />
          {/* Right bar */}
          <div
            className="absolute right-0 bg-black/70 transition-all duration-300"
            style={{
              top: `${spotlightPosition.top}px`,
              left: `${spotlightPosition.left + spotlightPosition.width}px`,
              height: `${spotlightPosition.height}px`,
            }}
            onClick={onClose}
          />
        </>
      ) : (
        /* Full backdrop when no spotlight */
        <div
          className="absolute inset-0 bg-black/70 transition-all duration-300"
          onClick={onClose}
        />
      )}

      {/* Spotlight border (optional visual enhancement) */}
      {spotlightPosition && (
        <div
          className="absolute border-2 border-primary rounded-md transition-all duration-300 pointer-events-none"
          style={{
            top: `${spotlightPosition.top}px`,
            left: `${spotlightPosition.left}px`,
            width: `${spotlightPosition.width}px`,
            height: `${spotlightPosition.height}px`,
          }}
        />
      )}

      {/* Tour step card */}
      <Card
        className="shadow-2xl border-2"
        style={getCardPosition()}
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" data-testid="badge-step-count">
                  Step {currentStepIndex + 1} of {steps.length}
                </Badge>
                {currentStep.isOptional && (
                  <Badge variant="outline" data-testid="badge-optional">
                    Optional
                  </Badge>
                )}
              </div>
              <CardTitle data-testid="text-step-title">{currentStep.title}</CardTitle>
              <CardDescription className="text-xs mt-1" data-testid="text-tour-title">
                {tourTitle}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="button-close-tour"
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <Progress value={progress} className="h-1.5 mt-3" data-testid="progress-tour" />
        </CardHeader>

        <CardContent className="space-y-4 overflow-y-auto max-h-[400px]">
          {currentStep.description && (
            <div 
              className="text-sm leading-relaxed whitespace-pre-wrap" 
              data-testid="text-step-content"
            >
              {currentStep.description}
            </div>
          )}

          {currentStep.videoUrl && (
            <div data-testid="video-embed">
              <VideoEmbed url={currentStep.videoUrl} />
            </div>
          )}

          {currentStep.targetSelector && !spotlightPosition && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> The target element for this step is not currently visible. 
              Please navigate to the appropriate page or section.
            </div>
          )}
        </CardContent>

        <CardFooter className="flex items-center justify-between gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={isFirstStep}
            data-testid="button-previous-step"
            className="flex-1 min-w-[100px]"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>

          {!isLastStep ? (
            <Button
              onClick={onNext}
              data-testid="button-next-step"
              className="flex-1 min-w-[100px]"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={onComplete}
              data-testid="button-complete-tour"
              className="flex-1 min-w-[100px]"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
